import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Brain, Shuffle, Quote, Flame, Trophy, ArrowRight, Sparkles, Layers, Grid3x3,
  HelpCircle, ChevronDown,
} from 'lucide-react';
import { fetchMyStats } from '../../services/gamesApi';
import { fetchSundaeToday, fetchSundaeStreak } from '../../services/sundaeApi';
import BibleTriviaGame from './BibleTriviaGame';
import VerseScrambleGame from './VerseScrambleGame';
import WhoSaidItGame from './WhoSaidItGame';
import VerseMatchGame from './VerseMatchGame';
import BibleCrosswordGame from './BibleCrosswordGame';
import GamesLeaderboard from './GamesLeaderboard';
import SundaeStartCard from './SundaeStartCard';
import SundaeRunner from './SundaeRunner';
import SundaeShareCard from './SundaeShareCard';
import SundaeLeaderboard from './SundaeLeaderboard';
import PastorModeView from './PastorModeView';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Bible Games hub — landing inside the Community → Games tab.
 * URL params:
 *  - ?sundae=start         → auto-launch the runner
 *  - ?sundae=YYYY-MM-DD    → show that day's group leaderboard at the top
 */
const GAME_CARDS = [
  {
    id: 'trivia',
    icon: Brain,
    title_en: 'Bible Trivia',
    title_es: 'Trivia Bíblica',
    desc_en: '5 questions a day · multiple choice',
    desc_es: '5 preguntas al día · opción múltiple',
    color: 'from-purple-500 to-indigo-500',
  },
  {
    id: 'scramble',
    icon: Shuffle,
    title_en: 'Verse Scramble',
    title_es: 'Reordenar Versículo',
    desc_en: 'Words shuffled · drag-tap to reorder',
    desc_es: 'Palabras revueltas · toca para reordenar',
    color: 'from-pink-500 to-rose-500',
  },
  {
    id: 'whosaidit',
    icon: Quote,
    title_en: 'Who Said It?',
    title_es: '¿Quién lo dijo?',
    desc_en: 'Match the famous biblical quote to the speaker',
    desc_es: 'Empareja la cita bíblica con quien la dijo',
    color: 'from-amber-500 to-orange-500',
  },
  {
    id: 'match',
    icon: Layers,
    title_en: 'Verse Match',
    title_es: 'Empareja Versículos',
    desc_en: 'Memory pair-match: verse text ↔ reference',
    desc_es: 'Empareja texto del versículo con su referencia',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'crossword',
    icon: Grid3x3,
    title_en: 'Bible Crossword',
    title_es: 'Crucigrama Bíblico',
    desc_en: 'Daily 7×7 puzzle · Bible-themed clues',
    desc_es: 'Crucigrama 7×7 diario · pistas bíblicas',
    color: 'from-blue-500 to-cyan-500',
  },
];

const GamesTab = ({ language = 'en' }) => {
  const { t } = useLanguage();
  // mode: 'hub' | 'trivia' | 'scramble' | 'whosaidit' | 'match' | 'crossword'
  //       | 'sundae' | 'sundae-result'
  const [mode, setMode] = useState('hub');
  const [stats, setStats] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [sundae, setSundae] = useState(null);
  const [sundaeFinal, setSundaeFinal] = useState(null);
  const [streak, setStreak] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const sundaeParam = searchParams.get('sundae');
  // Group-mode date param (YYYY-MM-DD) → render leaderboard at top.
  const groupModeDate =
    sundaeParam && /^\d{4}-\d{2}-\d{2}$/.test(sundaeParam) ? sundaeParam : null;

  // Pastor Mode — `?pastor=u1,u2,u3` renders a 7-day group rollup at the top
  // for the listed user_ids. No auth required (the endpoint is public).
  const pastorParam = searchParams.get('pastor');
  const pastorUserIds = pastorParam
    ? pastorParam.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const loadStats = useCallback(() => {
    fetchMyStats().then(setStats).catch(() => setStats(null));
  }, []);

  const loadSundae = useCallback(() => {
    fetchSundaeToday().then(setSundae).catch(() => setSundae(null));
    fetchSundaeStreak().then(setStreak).catch(() => setStreak(null));
  }, []);

  useEffect(() => {
    loadStats();
    loadSundae();
  }, [loadStats, loadSundae]);

  // Auto-launch sundae runner when ?sundae=start is in the URL.
  // If the user already completed today's sundae, fast-track to the share
  // card instead — otherwise SundaeRunner would mount with next_game=null
  // and render an empty shell.
  useEffect(() => {
    if (sundaeParam !== 'start' || mode !== 'hub' || !sundae) return;
    if (sundae.completed_at) {
      setSundaeFinal(sundae);
      setMode('sundae-result');
      const next = new URLSearchParams(searchParams);
      next.delete('sundae');
      setSearchParams(next, { replace: true });
      return;
    }
    setMode('sundae');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sundaeParam, sundae?.completed_at]);

  const back = () => {
    setMode('hub');
    loadStats();
    loadSundae();
  };

  const launchSundae = () => {
    if (sundae?.completed_at) {
      setSundaeFinal(sundae);
      setMode('sundae-result');
      return;
    }
    setMode('sundae');
    if (sundaeParam !== 'start') {
      const next = new URLSearchParams(searchParams);
      next.set('sundae', 'start');
      setSearchParams(next, { replace: true });
    }
  };

  const exitSundae = () => {
    if (sundaeParam === 'start') {
      const next = new URLSearchParams(searchParams);
      next.delete('sundae');
      setSearchParams(next, { replace: true });
    }
    back();
  };

  const handleAllComplete = (finalState) => {
    setSundaeFinal(finalState);
    setSundae(finalState);
    setMode('sundae-result');
    loadStats();
  };

  if (mode === 'sundae') {
    return (
      <SundaeRunner
        language={language}
        onAllComplete={handleAllComplete}
        onQuit={exitSundae}
        onScoreRefresh={loadStats}
      />
    );
  }

  if (mode === 'sundae-result' && sundaeFinal) {
    return (
      <SundaeShareCard
        language={language}
        state={sundaeFinal}
        onClose={exitSundae}
      />
    );
  }

  if (mode === 'trivia') return <BibleTriviaGame language={language} onBack={back} onScored={loadStats} />;
  if (mode === 'scramble') return <VerseScrambleGame language={language} onBack={back} onScored={loadStats} />;
  if (mode === 'whosaidit') return <WhoSaidItGame language={language} onBack={back} onScored={loadStats} />;
  if (mode === 'match') return <VerseMatchGame language={language} onBack={back} onScored={loadStats} />;
  if (mode === 'crossword') return <BibleCrosswordGame language={language} onBack={back} onScored={loadStats} />;

  return (
    <div className="space-y-6" data-testid="games-tab">
      {/* Group Mode banner — when ?sundae=YYYY-MM-DD points at a specific day */}
      {groupModeDate && (
        <SundaeLeaderboard
          language={language}
          date={groupModeDate}
        />
      )}

      {/* Pastor Mode — 7-day rolling group view */}
      {pastorUserIds.length > 0 && (
        <PastorModeView
          language={language}
          userIds={pastorUserIds}
        />
      )}

      {/* Daily Devotional Sundae — pinned at top */}
      {sundae && (
        <SundaeStartCard
          language={language}
          state={sundae}
          streak={streak}
          onStart={launchSundae}
          onView={() => {
            setSundaeFinal(sundae);
            setMode('sundae-result');
          }}
        />
      )}

      {/* Stats strip */}
      {stats && (
        <Card className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 border-purple-200/50 dark:border-purple-700/50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5" data-testid="games-stats-streak">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-bold tabular-nums">{stats.streak_days}</span>
                <span className="text-xs text-muted-foreground">
                  {t('games.hub.streak')}
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-testid="games-stats-total">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <span className="font-bold tabular-nums">{stats.total_score}</span>
                <span className="text-xs text-muted-foreground">
                  {t('games.hub.pts')}
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <span className="font-bold tabular-nums">{stats.games_played}</span>
                <span className="text-xs text-muted-foreground">
                  {t('games.hub.gamesPlayed')}
                </span>
              </div>
            </div>
            {stats.streak_days >= 3 && (
              <Badge className="bg-orange-500 text-white border-0 text-[10px]">
                <Flame className="w-3 h-3 mr-1" />
                {t('games.hub.onFire')}
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      {/* How to play — collapsible quick guide */}
      <Card className="border-purple-200/60 dark:border-purple-800/60" data-testid="games-howto-card">
        <button
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 p-4 text-left"
          data-testid="games-howto-toggle"
          aria-expanded={guideOpen}
        >
          <span className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold">{t('games.howToPlay.show')}</span>
            <span className="hidden sm:inline text-xs text-muted-foreground">
              · {t('games.howToPlay.subtitle')}
            </span>
          </span>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${guideOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {guideOpen && (
          <CardContent className="pt-0 pb-4 space-y-3" data-testid="games-howto-content">
            {GAME_CARDS.map((g) => {
              const Icon = g.icon;
              return (
                <div key={g.id} className="flex items-start gap-3" data-testid={`games-howto-${g.id}`}>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${g.color} text-white shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{g[`title_${language}`]}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t(`games.howToPlay.${g.id}`)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div className="flex items-start gap-3 pt-1 border-t border-border/60" data-testid="games-howto-tip">
              <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white shrink-0">
                <Flame className="w-4 h-4" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed self-center">
                {t('games.howToPlay.tip')}
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Game cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {GAME_CARDS.map((g) => {
          const Icon = g.icon;
          const playedToday = stats?.per_game?.[g.id]?.games_played > 0;
          return (
            <button
              key={g.id}
              onClick={() => setMode(g.id)}
              data-testid={`games-card-${g.id}`}
              className="text-left group"
            >
              <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-shadow">
                <div className={`bg-gradient-to-br ${g.color} p-5 text-white relative`}>
                  <Icon className="w-8 h-8 opacity-90" />
                  {playedToday && (
                    <Badge className="absolute top-2 right-2 bg-white/20 text-white border-white/30 text-[9px]">
                      {t('games.hub.playedToday')}
                    </Badge>
                  )}
                  <h3 className="font-bold text-base mt-3">
                    {g[`title_${language}`]}
                  </h3>
                  <p className="text-xs opacity-90 mt-0.5">
                    {g[`desc_${language}`]}
                  </p>
                </div>
                <CardContent className="p-3 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {t('games.hub.playNow')}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-purple-600 group-hover:translate-x-0.5 transition-transform" />
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>

      {/* Leaderboard */}
      <GamesLeaderboard language={language} />
    </div>
  );
};

export default GamesTab;
